import React from 'react';
import './Features.css';
import {
  FaCode,
  FaRocket,
  FaMobileAlt,
  FaShieldAlt,
  FaChartLine,
  FaCogs
} from 'react-icons/fa';

// O reveal por scroll é do observer único da aplicação (useReveal em App.tsx).
// Aqui só marcamos os alvos com .reveal e o índice do stagger.

const Features: React.FC = () => {
  const features = [
    {
      icon: <FaCode />,
      title: 'Desenvolvimento Web',
      description: 'Sites e aplicações web modernas, responsivas e otimizadas para performance.',
      gradient: 'enhanced-features-gradient-purple-blue',
      details: [
        'React, Next.js, TypeScript',
        'Design responsivo garantido',
        'SEO otimizado'
      ]
    },
    {
      icon: <FaRocket />,
      title: 'Automação de Processos',
      description: 'Automatize tarefas repetitivas e ganhe tempo para focar no que realmente importa.',
      gradient: 'enhanced-features-gradient-blue-cyan',
      details: [
        'Integração de sistemas',
        'APIs e webhooks',
        'Fluxos automatizados'
      ]
    },
    {
      icon: <FaMobileAlt />,
      title: 'Aplicativos Mobile',
      description: 'Apps nativos e híbridos para iOS e Android com experiência excepcional.',
      gradient: 'enhanced-features-gradient-cyan-teal',
      details: [
        'React Native',
        'Design nativo',
        'Performance otimizada'
      ]
    },
    {
      icon: <FaShieldAlt />,
      title: 'Segurança & Compliance',
      description: 'Proteção de dados e conformidade com LGPD e padrões internacionais.',
      gradient: 'enhanced-features-gradient-teal-green',
      details: [
        'Criptografia de dados',
        'Backups automáticos',
        'Conformidade LGPD'
      ]
    },
    {
      icon: <FaChartLine />,
      title: 'Analytics & BI',
      description: 'Dashboards e relatórios inteligentes para tomada de decisões baseadas em dados.',
      gradient: 'enhanced-features-gradient-green-purple',
      details: [
        'Dashboards interativos',
        'Relatórios personalizados',
        'Insights em tempo real'
      ]
    },
    {
      icon: <FaCogs />,
      title: 'Integração de Sistemas',
      description: 'Conecte diferentes plataformas e ferramentas para um fluxo de trabalho perfeito.',
      gradient: 'enhanced-features-gradient-purple-pink',
      details: [
        'APIs RESTful',
        'Sincronização de dados',
        'Middleware customizado'
      ]
    }
  ];

  const stats = [
    { number: '100+', label: 'Evoluções Versionadas' },
    { number: '70 mil+', label: 'Linhas Adicionadas' },
    { number: '5 anos', label: 'De Evolução Contínua' }
  ];

  const handleCtaClick = () => {
    const contactSection = document.querySelector('.cta-section, #contact, footer');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="enhanced-features-section">
      {/* Background Effects */}
      <div className="enhanced-features-bg-effects">
        <div className="enhanced-features-gradient-orb enhanced-features-gradient-orb-1"></div>
        <div className="enhanced-features-gradient-orb enhanced-features-gradient-orb-2"></div>
        <div className="enhanced-features-gradient-orb enhanced-features-gradient-orb-3"></div>
        <div className="enhanced-features-grid-pattern"></div>
      </div>

      <div className="enhanced-features-container">
        {/* Header */}
        <div className="enhanced-features-header reveal">
          <div className="enhanced-features-badge">
            <span>✨ Nossas Soluções</span>
          </div>
          <h2 className="enhanced-features-title">
            Tecnologia que{' '}
            <span className="enhanced-features-gradient-text">transforma negócios</span>
          </h2>
          <p className="enhanced-features-subtitle">
            Soluções completas de desenvolvimento e automação pensadas para 
            impulsionar seu crescimento e simplificar seus processos.
          </p>
        </div>

        {/* Stats */}
        <div className="enhanced-features-stats-grid">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="enhanced-features-stat-card reveal"
              style={{ '--i': index } as React.CSSProperties}
            >
              <div className="enhanced-features-stat-card-inner">
                <div className="enhanced-features-stat-number">{stat.number}</div>
                <div className="enhanced-features-stat-label">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="enhanced-features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className="enhanced-features-card reveal"
              style={{ '--i': index } as React.CSSProperties}
            >
              <div className="enhanced-features-card-inner">
                <div className={`enhanced-features-icon-wrapper ${feature.gradient}`}>
                  <div className="enhanced-features-icon">{feature.icon}</div>
                </div>
                
                <h3 className="enhanced-features-card-title">{feature.title}</h3>
                <p className="enhanced-features-card-description">{feature.description}</p>
                
                <div className="enhanced-features-details">
                  {feature.details.map((detail, idx) => (
                    <div key={idx} className="enhanced-features-detail">
                      <span className="enhanced-features-detail-icon">✓</span>
                      <span className="enhanced-features-detail-text">{detail}</span>
                    </div>
                  ))}
                </div>

                <div className="enhanced-features-floating-element enhanced-features-floating-element-1"></div>
                <div className="enhanced-features-floating-element enhanced-features-floating-element-2"></div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="enhanced-features-cta-section reveal reveal--scale">
          <div className="enhanced-features-cta-card">
            <h3 className="enhanced-features-cta-title">
              Pronto para começar seu projeto?
            </h3>
            <p className="enhanced-features-cta-description">
              Transforme suas ideias em realidade com nossa equipe de especialistas. 
              Consultoria gratuita e sem compromisso.
            </p>
            <button 
              className="enhanced-features-cta-button"
              onClick={handleCtaClick}
              aria-label="Falar com especialista"
            >
              Falar com Especialista
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
